import { useMemo } from 'react';
import { ContentCopy, Refresh } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  IconButton,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import type { ConversionJob, RawUploadGroup, ResourceImportSessionSummary } from '../store/hooks';

type ChipColor = 'default' | 'info' | 'success' | 'warning' | 'error';

/** A source retains its upload identity across separately identified outputs. */
interface MigrationUnit {
  id: string;
  label: string;
  fileCount?: number;
  sizeBytes?: number;
  rawPrefix?: string;
  sessionId?: string;
  status?: string;
  stats?: Record<string, number>;
  createdAt?: string | null;
  conversion?: ConversionJob;
}

interface MigrationImportsListProps {
  uploads: RawUploadGroup[];
  sessions: ResourceImportSessionSummary[];
  isLoading: boolean;
  onRefresh: () => void;
  onOpenBatch: (sessionId: string) => void;
  /** Add more files to a still-pre-conversion batch (raw upload id). */
  onAddFiles: (uploadId: string) => void;
  conversionJobs: ConversionJob[];
  conversionStatusKnown: boolean;
  conversionEnabled: boolean;
  onGo: (uploadId: string, retryOf?: string) => void;
  launchingSourceId: string | null;
  uploadsBusy: boolean;
}

function formatBytes(bytes?: number): string {
  if (!bytes) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/** Legacy sources can be linked by the trailing converted batch ID. */
function batchIdOf(s3Prefix?: string | null): string {
  if (!s3Prefix) return '';
  const parts = s3Prefix.split('/').filter(Boolean);
  return parts[parts.length - 1] ?? '';
}

/** Map the staging status onto the operator-facing lifecycle stage. */
function lifecycle(unit: MigrationUnit): { label: string; color: ChipColor } {
  if (!unit.sessionId) {
    const job = unit.conversion;
    if (!job) return { label: 'Uploaded', color: 'default' };
    if (job.status === 'failed' || job.status === 'dead')
      return { label: job.published ? 'Needs attention' : 'Conversion failed', color: 'error' };
    if (job.status === 'pending') return { label: 'Queued', color: 'info' };
    if (job.published || job.status === 'completed') return { label: 'Staging', color: 'info' };
    if (job.phase === 'verifying') return { label: 'Verifying', color: 'info' };
    if (job.phase === 'publishing_verified_output')
      return { label: 'Uploading results', color: 'info' };
    return { label: 'Converting', color: 'info' };
  }
  switch (unit.status) {
    case 'ready_for_review':
      return { label: 'Ready to review', color: 'warning' };
    case 'importing':
      return { label: 'Committing', color: 'info' };
    case 'completed':
      return { label: 'Committed', color: 'success' };
    case 'failed':
      return { label: 'Failed', color: 'error' };
    case 'ingesting':
      return { label: 'Staging', color: 'info' };
    default:
      return { label: unit.status ?? 'Staged', color: 'info' };
  }
}

/**
 * One unified list of migration units. An "upload" and the "batch" it converts
 * into are two phases of the same source, so each row shows a
 * single source progressing Uploaded → Ready to review → Committed, rather than
 * exposing the raw-uploads and staged-batches tables separately.
 */
export function MigrationImportsList({
  uploads,
  sessions,
  isLoading,
  onRefresh,
  onOpenBatch,
  onAddFiles,
  conversionJobs,
  conversionStatusKnown,
  conversionEnabled,
  onGo,
  launchingSourceId,
  uploadsBusy,
}: MigrationImportsListProps) {
  const { enqueueSnackbar } = useSnackbar();

  const units = useMemo<MigrationUnit[]>(() => {
    const byId = new Map<string, MigrationUnit>();
    for (const u of uploads) {
      byId.set(u.upload_id, {
        id: u.upload_id,
        label: u.label || u.upload_id,
        fileCount: u.file_count,
        sizeBytes: u.total_size_bytes,
        rawPrefix: u.prefix,
      });
    }
    for (const job of conversionJobs) {
      const existing = byId.get(job.raw_batch_id);
      if (existing)
        byId.set(job.raw_batch_id, { ...existing, conversion: job, createdAt: job.created_at });
    }
    // Keep the latest session if an older source has multiple staging attempts.
    const latestFirst = [...sessions].sort(
      (a, b) =>
        (b.created_at ?? '').localeCompare(a.created_at ?? '') ||
        b.import_session_id.localeCompare(a.import_session_id),
    );
    for (const s of latestFirst) {
      const outputId = batchIdOf(s.s3_prefix);
      const conversion = conversionJobs.find(
        job =>
          job.published &&
          job.verified &&
          (job.import_session_id === s.import_session_id || job.output_batch_id === outputId),
      );
      const id = s.raw_batch_id ?? conversion?.raw_batch_id ?? outputId;
      if (!id) continue;
      const existing = byId.get(id) ?? { id, label: id };
      if (existing.sessionId) continue;
      byId.set(id, {
        ...existing,
        sessionId: s.import_session_id,
        status: s.status,
        stats: s.import_stats ?? undefined,
        createdAt: s.created_at,
      });
    }
    // Newest first when we have a timestamp; otherwise stable by label.
    return [...byId.values()].sort((a, b) => {
      if (a.createdAt && b.createdAt) return b.createdAt.localeCompare(a.createdAt);
      if (a.createdAt) return -1;
      if (b.createdAt) return 1;
      return a.label.localeCompare(b.label);
    });
  }, [uploads, sessions, conversionJobs]);

  const copyPrefix = (prefix: string) => {
    void navigator.clipboard.writeText(prefix);
    enqueueSnackbar('Copied S3 prefix', { variant: 'success' });
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Imports"
        subheader="Upload files, press Go to convert and stage them, then review and commit the results."
        action={
          <Tooltip title="Refresh">
            <IconButton onClick={onRefresh} aria-label="Refresh imports">
              <Refresh />
            </IconButton>
          </Tooltip>
        }
      />
      <CardContent>
        {conversionStatusKnown && !conversionEnabled && uploads.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Conversion is not available here yet. You can continue uploading files.
          </Alert>
        )}
        {!conversionStatusKnown && uploads.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Conversion status is unavailable. Refresh before starting a conversion or adding files.
          </Alert>
        )}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : units.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No imports yet. Upload source files above to start one.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Source</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Files</TableCell>
                <TableCell align="right">Resources</TableCell>
                <TableCell align="right">Size</TableCell>
                <TableCell>Created</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {units.map(unit => {
                const stage =
                  !conversionStatusKnown && !unit.sessionId && !unit.conversion
                    ? { label: 'Status unavailable', color: 'default' as const }
                    : lifecycle(unit);
                const staged = unit.stats?.staged;
                const total = unit.stats?.total;
                const openable = Boolean(unit.sessionId);
                const job = unit.conversion;
                const retryable =
                  job && !job.published && (job.status === 'failed' || job.status === 'dead');
                const canStart = !job || retryable;
                return (
                  <TableRow key={unit.id} hover>
                    <TableCell>
                      {openable ? (
                        <MuiLink
                          component="button"
                          type="button"
                          disabled={uploadsBusy || Boolean(launchingSourceId)}
                          onClick={() => onOpenBatch(unit.sessionId as string)}
                          sx={{ textAlign: 'left' }}
                        >
                          {unit.label}
                        </MuiLink>
                      ) : (
                        unit.label
                      )}
                    </TableCell>
                    <TableCell>
                      <Box role="status">
                        <Chip
                          size="small"
                          label={launchingSourceId === unit.id ? 'Starting…' : stage.label}
                          color={stage.color}
                        />
                      </Box>
                      {job?.published &&
                        (job.status === 'failed' || job.status === 'dead') &&
                        !unit.sessionId && (
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            Results uploaded; ingestion needs checking.
                          </Typography>
                        )}
                    </TableCell>
                    <TableCell align="right">{unit.fileCount ?? '—'}</TableCell>
                    <TableCell align="right">
                      {total != null ? (staged != null ? `${staged}/${total}` : total) : '—'}
                    </TableCell>
                    <TableCell align="right">{formatBytes(unit.sizeBytes)}</TableCell>
                    <TableCell>
                      {unit.createdAt ? new Date(unit.createdAt).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {openable ? (
                        <MuiLink
                          component="button"
                          type="button"
                          disabled={uploadsBusy || Boolean(launchingSourceId)}
                          onClick={() => onOpenBatch(unit.sessionId as string)}
                        >
                          {unit.status === 'completed'
                            ? 'View'
                            : unit.status === 'ready_for_review'
                              ? 'Review & commit'
                              : 'View progress'}
                        </MuiLink>
                      ) : unit.rawPrefix ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 1,
                          }}
                        >
                          {canStart && (
                            <Button
                              variant="contained"
                              size="small"
                              disabled={
                                !conversionEnabled || !conversionStatusKnown || uploadsBusy || Boolean(launchingSourceId)
                              }
                              onClick={() => onGo(unit.id, retryable ? job.job_id : undefined)}
                            >
                              {retryable ? 'Retry conversion' : 'Go'}
                            </Button>
                          )}
                          {!job && (
                            <MuiLink
                              component="button"
                              type="button"
                              disabled={
                                !conversionStatusKnown || uploadsBusy || Boolean(launchingSourceId)
                              }
                              onClick={() => onAddFiles(unit.id)}
                              sx={{
                                '&:disabled': { color: 'text.disabled', textDecoration: 'none' },
                              }}
                            >
                              Add files
                            </MuiLink>
                          )}
                          <Tooltip title="Copy source location">
                            <IconButton
                              size="small"
                              aria-label="Copy S3 prefix"
                              onClick={() => copyPrefix(unit.rawPrefix as string)}
                            >
                              <ContentCopy fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
